// Sub-position within the `'from'` clause: did the cursor land RIGHT AFTER a
// completed table reference (or its alias)? The grammar model can only tell
// us the cursor sits inside a FROM/JOIN clause; it cannot distinguish the
// "expecting a relation name" sub-position from the "just finished naming a
// relation" sub-position. They demand different suggestion sets:
//
//   "SELECT * FROM "                  ← expecting a relation → tables / schemas
//   "SELECT * FROM x, "               ← expecting a relation → tables / schemas
//   "SELECT * FROM Sales."            ← dotted-access (handled elsewhere)
//   "SELECT * FROM x "                ← just finished naming → keywords
//   "SELECT * FROM x AS y "           ← alias bound → keywords
//   "SELECT * FROM x JOIN y "         ← second relation finished → keywords
//
// At the "just finished" position another bare table name is a syntax error
// (a comma would have to come first), so the resolver must emit continuation
// keywords (WHERE / JOIN / ON / ORDER / GROUP / HAVING / UNION / AS / …)
// instead of more tables.
//
// This module is a pure text scan — it does not consult the grammar parse
// tree, so it can run on the resolver's hot path without paying for a parse.

/** Keywords that, when they are the last token before the cursor's leading
 *  whitespace, mean the user is still EXPECTING a relation (or alias) — so
 *  the cursor is NOT at the "after-completed-relation" sub-position. */
const RELATION_EXPECTING_KEYWORDS: ReadonlySet<string> = new Set([
  // Relation introducers.
  'FROM',
  'JOIN',
  'USING',
  'INTO',
  'UPDATE',
  'TABLE',
  // After AS the user is naming the alias — not yet a completed relation.
  'AS',
  // After APPLY (T-SQL CROSS APPLY / OUTER APPLY) we expect a relation.
  'APPLY',
  // After LATERAL (PG / Oracle) we expect a relation.
  'LATERAL',
  // `ON` names an OBJECT in the statements that reach a `from` position with it
  // trailing — `GRANT SELECT ON ▮`, `COMMENT ON ▮`, `CREATE INDEX … ON ▮`. (A
  // join's `ON` yields clause 'join-on' and never reaches this walk.)
  'ON',
]);

/**
 * Returns `true` when the cursor sits immediately after a completed relation
 * name or its alias inside the FROM list (or JOIN list).
 *
 * Caller must have already confirmed `positionKind === 'from'`. The function
 * does not validate clause context itself — it only inspects the last
 * non-whitespace token before the cursor.
 *
 *   "SELECT * FROM x " → true
 *   "SELECT * FROM x JOIN y " → true
 *   "SELECT * FROM x AS y " → true
 *   "SELECT * FROM " → false (last token is FROM)
 *   "SELECT * FROM x AS " → false (last token is AS — expecting alias)
 *   "SELECT * FROM x," → false (last char is comma)
 *   "SELECT * FROM x" → false (no trailing whitespace — user is mid-type)
 */
export function isAfterCompletedFromRelation(
  text: string,
  offset: number,
  statementStart: number,
): boolean {
  let i = Math.min(offset, text.length);
  // Skip whitespace immediately before the cursor.
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  // No whitespace before cursor → user is mid-identifier; defer to the typed
  // prefix ranker, which already handles table-name completion.
  if (i === offset) return false;
  if (i <= statementStart) return false;

  const prevChar = text[i - 1] ?? '';

  // Completed bracket/quoted identifier — `[Order Details] ` or `"foo" ` or
  // backtick close. Anything ending in a closing identifier delimiter
  // counts as a finished relation.
  if (prevChar === ']' || prevChar === '"' || prevChar === '`') return true;

  // Bare identifier end → walk back to extract the word and inspect it.
  if (/[A-Za-z0-9_]/.test(prevChar)) {
    let j = i;
    while (j > statementStart && /[A-Za-z0-9_]/.test(text[j - 1] ?? '')) j -= 1;
    const word = text.slice(j, i).toUpperCase();
    return !RELATION_EXPECTING_KEYWORDS.has(word);
  }

  // Anything else (comma, paren, semicolon, dot, operator) — not after a
  // completed relation.
  return false;
}
